import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminBorderUprightMapping } from "../../../../../http/border/types/uprightMappings";
import { useUprightMappingsApi } from "../../../../../http/border/useUprightMappings";
import { PaginationQuery } from "../../../../../types";
import { formatBoolean, itemLabel } from "../../../../../utils";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import CreateUprightMappingModalDialogButton from "./CreateUprightMappingModalDialogButton";
import UprightMappingForm from "./UprightMappingForm";

const formatNumber = (value: number | null | undefined, unit?: string) => {
  if (value === null || value === undefined) {
    return "";
  }

  return unit ? `${value} ${unit}` : value;
};

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const UprightMappings: FC<Props> = ({ active }) => {
  const { borderSystemId } = useParams<{ borderSystemId: string }>();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "uprightMappings" });
  const aborter = useRef(new AbortController());
  const uprightMappingsApi = useUprightMappingsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "uprightMappings");

  const isActiveTab = active ?? true;
  const [itemToEdit, setItemToEdit] = useState<AdminBorderUprightMapping>();
  const [itemToDelete, setItemToDelete] = useState<AdminBorderUprightMapping>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = uprightMappingsApi.useAdminUprightMappings(borderSystemId!, query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.uprightMappings.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.uprightMappings.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateUprightMappingModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminBorderUprightMapping>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.normatives.singular}</th>
            <th align="left">{i18n.translation.common.guardrailComposition}</th>
            <th align="left">{i18n.translation.uprights.singular}</th>
            <th align="left">{i18n.translation.common.isGateVisible}</th>
            <th align="right">{i18n.translation.common.minCurbHeight}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <Table.Td noWrap>{item.systemNormative.normative.name}</Table.Td>
            <td>{item.guardrailComposition.name}</td>
            <Table.Td noWrap>{itemLabel(item.upright.item)}</Table.Td>
            <td>{formatBoolean(i18n.translation, item.isGateVisible)}</td>
            <td align="right">{formatNumber(item.minCurbHeight, "mm")}</td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.uprightMappings.edit}>
          <UprightMappingForm uprightMapping={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.uprightMappings.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                uprightMappingsApi
                  .deleteAdminUprightMapping(borderSystemId!, itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.uprightMappings.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default UprightMappings;
